import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, Btn } from "./UI";
import { Gift, TicketPercent, Coins, Loader2, Plus, History } from "lucide-react";
import { toast } from "sonner";

const usd = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const errMsg = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };
const KIND = { gift_card: { label: "eGift Card", icon: Gift }, coupon: { label: "Coupon", icon: TicketPercent }, campaign_credit: { label: "Campaign Credit", icon: Coins } };
const STATUS = {
  claimed: "border-cyan-400/25 bg-cyan-400/5 text-cyan-300", held: "border-amber-400/25 bg-amber-400/5 text-amber-300",
  redeemed: "border-emerald-400/25 bg-emerald-400/5 text-emerald-300", void: "border-rose-400/25 bg-rose-400/5 text-rose-300",
};
const LEDGER = {
  issue: "Issued", claim: "Claimed", hold: "Reserved for launch", release: "Returned to wallet", redeem: "Applied to launch", void: "Voided",
};

export const PromoWalletCard = () => {
  const [w, setW] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLedger, setShowLedger] = useState(false);
  const [redeemError, setRedeemError] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/wallet"); setW(data); } catch { setW({ balance: 0, cards: [], ledger: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const redeem = async () => {
    if (!code.trim()) return;
    setBusy(true); setRedeemError("");
    try {
      const { data } = await api.post("/wallet/claim", { code: code.trim() });
      toast.success(data.result === "already_yours" ? "That code is already in your wallet" : "Credit added to your wallet");
      setCode(""); load();
    } catch (e) {
      const msg = errMsg(e, "Could not redeem that code");
      setRedeemError(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  const active = (w?.cards || []).filter((c) => ["claimed", "held"].includes(c.status) && !c.expired);
  const past = (w?.cards || []).filter((c) => !active.includes(c));

  return (
    <Card data-testid="promo-wallet-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400/20 to-blue-500/10 border border-cyan-400/30 flex items-center justify-center">
            <Gift className="w-5 h-5 text-cyan-300" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 font-medium">Promotional credit</div>
            <div className="font-display text-2xl font-bold" data-testid="wallet-balance">{w ? usd(w.balance) : "—"}
              {w?.held > 0 && <span className="text-xs text-amber-300 font-normal ml-2" data-testid="wallet-held">+ {usd(w.held)} reserved for a pending launch</span>}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">Auto-applied at Approve &amp; Launch · card fee only on the remaining balance · never applies to fundraising</div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
        <div className="flex items-end gap-2">
          <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setRedeemError(""); }} placeholder="Have a code? TR-XXXX-XXXX"
            className="w-56 bg-slate-900/60 border border-white/10 rounded-md px-3 py-2 text-sm font-mono text-white focus:border-cyan-400 focus:outline-none" data-testid="wallet-code-input"
            onKeyDown={(e) => e.key === "Enter" && redeem()} />
          <Btn onClick={redeem} disabled={busy || !code.trim()} data-testid="wallet-redeem-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Redeem</Btn>
        </div>
        {redeemError && <div className="text-[11px] text-rose-300" data-testid="wallet-redeem-error">{redeemError}</div>}
        </div>
      </div>

      {w && (active.length > 0 || past.length > 0) && (
        <div className="mt-4 space-y-1.5" data-testid="wallet-cards">
          {[...active, ...past].map((c) => {
            const meta = KIND[c.kind] || KIND.gift_card; const Icon = meta.icon;
            return (
              <div key={c.id} className={`flex items-center gap-3 rounded-lg border border-white/10 px-3 py-2 text-xs ${past.includes(c) ? "opacity-60" : ""}`} data-testid={`wallet-card-${c.code}`}>
                <Icon className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
                <span className="font-mono text-slate-200">{c.code}</span>
                <span className="text-slate-400 truncate">{c.promotion_name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS[c.status] || "border-white/10 text-slate-400"}`}>{c.expired && c.status !== "redeemed" ? "expired" : c.status}</span>
                <span className="ml-auto text-slate-200 font-semibold">
                  {c.kind === "coupon" ? (c.value_type === "percent" ? `${c.value}% off` : usd(c.value)) : `${usd(c.remaining_value)} left`}
                </span>
                <span className="text-slate-500 w-28 text-right">{c.expires_at ? `exp ${c.expires_at.slice(0, 10)}` : "no expiry"}</span>
              </div>
            );
          })}
        </div>
      )}
      {w && (w.cards || []).length === 0 && <div className="mt-3 text-xs text-slate-500" data-testid="wallet-empty">No promotional credit yet — scan a TotalReach gift card QR or enter a code above.</div>}

      {w && (w.ledger || []).length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowLedger(!showLedger)} className="text-[11px] text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1" data-testid="wallet-ledger-toggle">
            <History className="w-3 h-3" /> {showLedger ? "Hide" : "Show"} ledger ({w.ledger.length})
          </button>
          {showLedger && (
            <div className="mt-2 rounded-lg border border-white/10 overflow-hidden" data-testid="wallet-ledger">
              <table className="w-full text-[11px]">
                <thead><tr className="text-left text-slate-500 uppercase tracking-wider border-b border-white/5"><th className="px-3 py-1.5">When</th><th className="px-3 py-1.5">Code</th><th className="px-3 py-1.5">Event</th><th className="px-3 py-1.5 text-right">Amount</th><th className="px-3 py-1.5 text-right">Balance</th></tr></thead>
                <tbody>
                  {w.ledger.filter((l) => l.type !== "issue").map((l) => (
                    <tr key={l.id} className="border-b border-white/5" data-testid={`ledger-row-${l.id}`}>
                      <td className="px-3 py-1.5 text-slate-400">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-1.5 font-mono text-slate-300">{l.code}</td>
                      <td className="px-3 py-1.5 text-slate-200">{LEDGER[l.type] || l.type}{l.type === "redeem" && l.note?.includes("—") ? <span className="text-slate-500"> · {l.note.split("—").slice(1).join("—").trim()}</span> : null}</td>
                      <td className={`px-3 py-1.5 text-right font-mono ${l.amount < 0 ? "text-amber-300" : l.amount > 0 ? "text-emerald-300" : "text-slate-400"}`}>{l.amount ? (l.amount > 0 ? "+" : "−") + usd(Math.abs(l.amount)) : "—"}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-slate-300">{l.balance_after != null ? usd(l.balance_after) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
